/**
 * The client's editing state over the synced graph: one graph, one undo history and one caret
 * for every view on screen (journal days, node views). Ported from the editor spike (spike A),
 * with three changes: every op runs against the root of the view the block is in; ↑/↓ move across
 * the rows of the whole view (so they cross day boundaries); and an empty day or page shows a
 * ghost row that becomes a real block (and journal day) on the first keystroke, so merely opening
 * the app never creates nodes.
 *
 * Typing never touches React state: the active textarea is uncontrolled and is committed as one
 * content op after 1 s idle, on blur, or before any structural op or undo (D37).
 *
 * Links (SPEC §6.2): every transaction that writes text also creates the pages its new refs name,
 * except the ref around the caret while the block is still being typed in (nothing is created
 * while the caret is inside the brackets). A page created that way during one stay in a block, and
 * no longer referenced or filled before leaving it, is removed again (a typo never leaves a page).
 */
import {
  readEditorOptions,
  SETTINGS_EDITOR,
  SETTINGS_ID,
  ME_FIELD,
  meOf,
  cycleStatus,
  duplicate,
  editorOptions,
  hasTag,
  mergedTarget,
  SCHEMA_FIELD,
  stableJson,
  TAG,
  TaskIndex,
  toggleDone,
  type Schema,
  type TagEnv,
  isEmptyPage,
  linkPageOps,
  normalizeTitle,
  RefIndex,
  refsOf,
  refSyntax,
  renameOps,
  scanReferrers,
  titleKey,
  TitleIndex,
  titleProblem,
  hasLiveChildren,
  History,
  indent,
  journalId,
  makeNode,
  merge,
  moveDown,
  moveUp,
  op,
  outdent,
  remove,
  setCollapsed,
  split,
  uuidv7,
  zoomOut as coreZoomOut,
  keysAtEnd,
  cryptoRand,
  type Caret,
  type Ctx,
  type EditorOptions,
  type Graph,
  type NodeId,
  type OpBody,
  type Result,
} from '@solar/core'
import { JournalIndex } from './journal-index.ts'
import { detect, pickerItems, type PickerContext, type PickerItem } from './picker.ts'
import { SearchClient } from './search-client.ts'
import type { SyncClient } from './sync-client.ts'

export type Mode =
  | { readonly kind: 'idle' }
  | { readonly kind: 'edit'; readonly id: string; readonly root: NodeId }
  | { readonly kind: 'select'; readonly root: NodeId; readonly anchor: NodeId; readonly head: NodeId }

export interface CaretRequest extends Caret {
  readonly seq: number
}

/** A row on screen. Views give the store their flat row list for cross-row navigation. */
export type ViewRow =
  | { readonly type: 'day'; readonly key: string; readonly date: string }
  | { readonly type: 'block'; readonly key: string; readonly id: NodeId; readonly depth: number; readonly root: NodeId }
  | { readonly type: 'ghost'; readonly key: string; readonly id: string; readonly root: NodeId; readonly date: string | null }
  /** A view-specific row (today's task strip, a page's head with tags, fields and schema). */
  | { readonly type: 'extra'; readonly key: string; readonly what: 'strip' | 'pagehead' | 'entity'; readonly id: string }
  /** Section, group and path labels (references). Not editable, skipped by ↑/↓. */
  | {
      readonly type: 'label'
      readonly key: string
      readonly level: 'section' | 'group' | 'path'
      readonly text: string
      readonly href?: string
      readonly onToggle?: () => void
      readonly open?: boolean
    }

export interface PickerState extends PickerContext {
  readonly blockId: string
  readonly items: readonly PickerItem[]
  readonly active: number
}

export const ghostForDay = (date: string): string => `ghost:day:${date}`
export const ghostForNode = (id: NodeId): string => `ghost:node:${id}`
export const isGhost = (id: string): boolean => id.startsWith('ghost:')

const IDLE_COMMIT_MS = 1000

export type Visit = { readonly kind: 'node'; readonly id: NodeId } | { readonly kind: 'day'; readonly date: string }
export type Pane = { readonly key: string; readonly kind: 'node'; readonly id: NodeId } | { readonly key: string; readonly kind: 'calendar' }
export interface Sidebar {
  readonly open: boolean
  readonly width: number
  readonly panes: readonly Pane[]
  readonly left: boolean
}

const RECENT_KEY = 'solar-recent'
const SIDEBAR_KEY = 'solar-sidebar'

function loadRecent(): Visit[] {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as Visit[]
    return Array.isArray(v) ? v.slice(0, 30) : []
  } catch {
    return []
  }
}

function loadSidebar(): Sidebar {
  const fallback: Sidebar = { open: false, width: 380, panes: [], left: true }
  try {
    const v = JSON.parse(localStorage.getItem(SIDEBAR_KEY) ?? 'null') as Partial<Sidebar> | null
    if (!v) return fallback
    return { open: v.open === true, width: Math.min(700, Math.max(280, Number(v.width) || 380)), panes: Array.isArray(v.panes) ? v.panes.slice(0, 8) : [], left: v.left !== false }
  } catch {
    return fallback
  }
}

export interface Navigator {
  /** Open a node (zoom) or a day; supplied by the router. */
  openNode(id: NodeId): void
  openJournal(date?: string): void
}

export class ClientStore {
  readonly sync: SyncClient
  readonly g: Graph
  readonly history: History
  readonly journal: JournalIndex
  readonly titles: TitleIndex
  readonly refs: RefIndex
  readonly tasks: TaskIndex
  readonly tagEnv: TagEnv
  /** The person marked "this is me" (`@me` in queries, the open-task strip). */
  get me(): NodeId | null {
    return meOf(this.g, this.titles.nodes())
  }
  /** Incremented when any tag's schema changes (field grids re-read their fields). */
  schemasVersion = 0
  private readonly schemaSubs = new Set<() => void>()
  private readonly withSchema = new Set<NodeId>()
  /** Editor behaviour (Settings → Editor), kept in the settings node so it follows you (D45). */
  options: EditorOptions = editorOptions()
  mode: Mode = { kind: 'idle' }
  caretReq: CaretRequest | null = null
  picker: PickerState | null = null
  /** Pages created from links while editing `block` (removed again if the link goes away). */
  private session: { block: string; pages: Set<NodeId> } | null = null
  textarea: HTMLTextAreaElement | null = null
  nav: Navigator = { openNode: () => {}, openJournal: () => {} }
  /** Row lists of the views on screen (main, sidebar panes), by view key. */
  readonly views = new Map<string, () => readonly ViewRow[]>()
  /** The view the last pointer or keyboard interaction happened in; ↑/↓ stay inside it. */
  activeView = 'main'
  /** Rows of the active view (for cross-row navigation and selection). */
  rows = (): readonly ViewRow[] => this.views.get(this.activeView)?.() ?? this.views.get('main')?.() ?? []
  /** Recently visited (SPEC §7.1, left sidebar), newest first; per device. */
  recent: Visit[] = loadRecent()
  /** Right sidebar (SPEC §3.2): open panes, newest first, and its width; per device. */
  sidebar: Sidebar = loadSidebar()
  readonly search: SearchClient
  uiVersion = 0
  private seq = 0
  private draft: { id: NodeId; caretBefore: Caret; timer: ReturnType<typeof setTimeout> | null } | null = null
  private burstCaret: Caret | null = null
  private readonly nodeSubs = new Map<NodeId, Set<() => void>>()
  private readonly structureSubs = new Set<() => void>()
  private readonly uiSubs = new Set<() => void>()

  constructor(sync: SyncClient) {
    this.sync = sync
    this.g = sync.g
    this.history = new History(200, (ops, ts) => sync.local(ops, ts))
    this.journal = new JournalIndex(this.g)
    this.titles = new TitleIndex(this.g)
    this.refs = new RefIndex(this.g)
    this.tagEnv = { g: this.g, resolve: (t) => this.titles.resolve(t) }
    this.tasks = new TaskIndex(this.g, (n) => hasTag(this.tagEnv, n, mergedTarget(this.g, TAG.task)))
    this.search = new SearchClient(this.g)
    this.options = readEditorOptions(this.g.get(SETTINGS_ID)?.props[SETTINGS_EDITOR])
    this.g.subscribe((cs) => {
      if (!cs.nodes.has(SETTINGS_ID)) return
      this.options = readEditorOptions(this.g.get(SETTINGS_ID)?.props[SETTINGS_EDITOR])
      this.uiChanged()
    })
    // Leaving the page (or the app on a phone) commits what is being typed, before the sync client
    // writes and sends the outbox (SPEC §16.3: zero data loss).
    sync.onExit.add(() => this.commitDraft())
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.commitDraft()
      })
    }
    for (const n of this.g.nodes()) if (n.props[SCHEMA_FIELD] !== undefined) this.withSchema.add(n.id)
    this.g.subscribe((cs) => {
      let changed = false
      for (const id of cs.nodes) {
        const n = this.g.get(id)
        const has = n !== undefined && n.deletedAt === null && n.props[SCHEMA_FIELD] !== undefined
        if (has || this.withSchema.has(id)) changed = true
        if (has) this.withSchema.add(id)
        else this.withSchema.delete(id)
      }
      if (changed) {
        this.schemasVersion++
        this.schemaSubs.forEach((f) => f())
      }
    })
    this.g.subscribe((cs) => {
      for (const id of cs.nodes) this.nodeSubs.get(id)?.forEach((f) => f())
      if (cs.structural) this.structureSubs.forEach((f) => f())
    })
  }

  // ------------------------------------------------------------------ subscriptions

  subscribeNode = (id: NodeId, fn: () => void): (() => void) => {
    let s = this.nodeSubs.get(id)
    if (!s) this.nodeSubs.set(id, (s = new Set()))
    s.add(fn)
    return () => {
      s.delete(fn)
      if (s.size === 0) this.nodeSubs.delete(id)
    }
  }

  subscribeStructure = (fn: () => void): (() => void) => {
    this.structureSubs.add(fn)
    return () => this.structureSubs.delete(fn)
  }

  subscribeSchemas = (fn: () => void): (() => void) => {
    this.schemaSubs.add(fn)
    return () => this.schemaSubs.delete(fn)
  }

  getSchemasVersion = (): number => this.schemasVersion

  subscribeUi = (fn: () => void): (() => void) => {
    this.uiSubs.add(fn)
    return () => this.uiSubs.delete(fn)
  }

  getStructureVersion = (): number => this.g.structureVersion
  getUiVersion = (): number => this.uiVersion

  private uiChanged(): void {
    this.uiVersion++
    this.uiSubs.forEach((f) => f())
  }

  hasChildren(id: NodeId): boolean {
    return hasLiveChildren(this.g, id)
  }

  /** Change an editor option on every device: a synced settings change, not an undo step. */
  setOption<K extends keyof EditorOptions>(key: K, value: EditorOptions[K]): void {
    this.options = { ...this.options, [key]: value }
    if (this.g.isLive(SETTINGS_ID)) this.sync.local([op.prop(SETTINGS_ID, SETTINGS_EDITOR, { ...this.options })], Date.now())
    this.uiChanged()
  }

  // ------------------------------------------------------------------ helpers

  ctx(root: NodeId): Ctx {
    return { g: this.g, root, newId: () => uuidv7(), rand: cryptoRand, now: Date.now(), options: this.options }
  }

  private get root(): NodeId | null {
    return this.mode.kind === 'idle' ? null : this.mode.root
  }

  currentCaret(): Caret | null {
    if (this.mode.kind !== 'edit') return null
    const ta = this.textarea
    if (ta && ta.dataset.id === this.mode.id) return { id: this.mode.id, start: ta.selectionStart, end: ta.selectionEnd }
    return { id: this.mode.id, start: 0, end: 0 }
  }

  textOf(id: string): string {
    const ta = this.textarea
    if (ta && ta.dataset.id === id) return ta.value
    return isGhost(id) ? '' : (this.g.get(id)?.content ?? '')
  }

  /** The view root a row belongs to (from the active rows). */
  private rootOf(id: string): NodeId | null {
    for (const r of this.rows()) if ((r.type === 'block' || r.type === 'ghost') && r.id === id) return r.root
    return null
  }

  setCaret(c: Caret | null, root?: NodeId): void {
    this.leaveBlock(c?.id ?? null)
    const r = c ? (root ?? this.rootOf(c.id) ?? this.root) : null
    if (c === null || r === null || (!isGhost(c.id) && !this.g.isLive(c.id))) {
      this.mode = { kind: 'idle' }
      this.caretReq = null
    } else {
      this.mode = { kind: 'edit', id: c.id, root: r }
      this.caretReq = { ...c, seq: ++this.seq }
    }
    this.uiChanged()
  }

  edit(id: string, root: NodeId, at: 'end' | number = 'end'): void {
    this.commitDraft()
    const len = this.textOf(id).length
    const pos = at === 'end' ? len : Math.min(at, len)
    this.setCaret({ id, start: pos, end: pos }, root)
  }

  setSelection(root: NodeId, anchor: NodeId, head: NodeId): void {
    this.commitDraft()
    this.leaveBlock(null)
    this.mode = { kind: 'select', root, anchor, head }
    this.caretReq = null
    this.uiChanged()
  }

  idle(): void {
    this.commitDraft()
    this.leaveBlock(null)
    if (this.mode.kind !== 'idle') {
      this.mode = { kind: 'idle' }
      this.caretReq = null
      this.uiChanged()
    }
  }

  /** Selected block ids in visible order, within the selection's root. */
  selectedIds(): NodeId[] {
    const m = this.mode
    if (m.kind === 'edit') return isGhost(m.id) ? [] : [m.id]
    if (m.kind !== 'select') return []
    const rows = this.rows().filter((r): r is Extract<ViewRow, { type: 'block' }> => r.type === 'block' && r.root === m.root)
    const a = rows.findIndex((r) => r.id === m.anchor)
    const h = rows.findIndex((r) => r.id === m.head)
    if (a < 0 || h < 0) return []
    const [lo, hi] = a <= h ? [a, h] : [h, a]
    return rows.slice(lo, hi + 1).map((r) => r.id)
  }

  // ------------------------------------------------------------------ typing

  noteKeyDown(ta: HTMLTextAreaElement): void {
    if (!this.draft || this.draft.id !== ta.dataset.id) {
      this.burstCaret = { id: ta.dataset.id!, start: ta.selectionStart, end: ta.selectionEnd }
    }
  }

  onInput(ta: HTMLTextAreaElement): void {
    this.noteInput(ta)
    this.updatePicker(ta)
  }

  /** Text changed in the active textarea: start or extend the typing burst. */
  private noteInput(ta: HTMLTextAreaElement): void {
    const id = ta.dataset.id!
    if (isGhost(id)) return this.materializeGhost(ta)
    if (this.draft && this.draft.id !== id) this.commitDraft()
    if (!this.draft) {
      const caretBefore = this.burstCaret?.id === id ? this.burstCaret : { id, start: ta.selectionStart, end: ta.selectionStart }
      this.draft = { id, caretBefore, timer: null }
    }
    if (this.draft.timer !== null) clearTimeout(this.draft.timer)
    this.draft.timer = setTimeout(() => this.commitDraft(true), IDLE_COMMIT_MS)
  }

  /**
   * Commit the typing burst as one content op. `staying`: the caret stays in the block (idle
   * autosave, structural ops, undo), so the ref around the caret creates no page yet.
   */
  commitDraft(staying = false): void {
    const d = this.draft
    if (!d) return
    if (d.timer !== null) clearTimeout(d.timer)
    this.draft = null
    if (!this.g.isLive(d.id)) return
    const text = this.textOf(d.id)
    if (text === this.g.must(d.id).content) return
    const caretAfter = this.currentCaret() ?? { id: d.id, start: text.length, end: text.length }
    this.commit('type', [op.content(d.id, text)], { caretBefore: d.caretBefore, caretAfter }, staying ? caretAfter : null)
  }

  hasDraft(id: string): boolean {
    return this.draft?.id === id
  }

  /**
   * First keystroke in a ghost row: create the journal day (if needed) and the block with the
   * typed text in one transaction, then move the caret into the real block.
   */
  private materializeGhost(ta: HTMLTextAreaElement): void {
    const ghost = ta.dataset.id!
    const text = ta.value
    const caret = ta.selectionStart
    const ops: OpBody[] = []
    let parent: NodeId
    if (ghost.startsWith('ghost:day:')) {
      const date = ghost.slice('ghost:day:'.length)
      parent = journalId(date)
      const day = this.g.get(parent)
      if (!day) ops.push(op.create(makeNode({ id: parent, kind: 'journal', date, order: 'a0', createdAt: Date.now(), updatedAt: Date.now() })))
      else if (day.deletedAt !== null) ops.push(op.restore(parent, [parent]))
    } else {
      parent = ghost.slice('ghost:node:'.length)
    }
    const [order] = keysAtEnd(this.g, parent, 1, cryptoRand)
    const id = uuidv7()
    ops.push(op.create(makeNode({ id, kind: 'block', parentId: parent, order: order!, content: text, createdAt: Date.now(), updatedAt: Date.now() })))
    const caretAfter = { id, start: caret, end: caret }
    this.commit('type', ops, { caretBefore: { id: ghost, start: 0, end: 0 }, caretAfter }, caretAfter)
    // The ghost's root is the day or the node itself, which is the new block's parent.
    this.setCaret({ id, start: caret, end: caret }, parent)
  }

  // ------------------------------------------------------------------ transactions

  /**
   * Apply ops as one undo unit, plus the pages their new refs name and the removal of pages this
   * stay in the block created and no longer needs. `caret`: the caret while the block is still
   * being typed in (its ref creates nothing yet), or null when leaving.
   */
  private commit(
    label: string,
    ops: readonly OpBody[],
    meta: { caretBefore?: Caret | null; caretAfter?: Caret | null },
    caret: Caret | null = null,
  ): void {
    if (ops.length === 0) return
    const all = [...ops, ...this.linkOps(ops, caret)]
    try {
      this.history.commit(this.g, all, Date.now(), { label, ...meta, rootBefore: this.root ?? undefined })
    } catch (e) {
      console.error('solar: could not apply', label, e)
    }
  }

  private linkOps(ops: readonly OpBody[], caret: Caret | null): OpBody[] {
    const out: OpBody[] = []
    // A page this stay in the block created whose link is gone again (a typo, a changed mind):
    // remove it, if it is still empty and nothing else refers to it.
    const s = this.session
    if (s) {
      const text = ops.find((o): o is Extract<OpBody, { type: 'node.content' }> => o.nodeId === s.block && o.type === 'node.content')
      for (const pageId of text ? [...s.pages] : []) {
        const page = this.g.get(pageId)
        if (!page || page.deletedAt !== null || page.title === null) {
          s.pages.delete(pageId)
          continue
        }
        if (this.mentions(text!.payload.content, page.title)) continue
        if (isEmptyPage(this.g, page) && scanReferrers(this.g, page.title).every((r) => r === s.block)) {
          out.push(op.delete(pageId))
          s.pages.delete(pageId)
        }
      }
    }
    const made = new Map<string, NodeId>()
    const now = Date.now()
    for (const o of ops) {
      const content = o.type === 'node.content' ? o.payload.content : o.type === 'node.create' ? o.payload.node.content : ''
      if (content === '') continue
      const creates = linkPageOps(content, {
        resolve: (t) => this.titles.resolve(t) ?? made.get(titleKey(t)) ?? null,
        newId: () => uuidv7(),
        now,
        caret: caret && caret.id === o.nodeId ? caret.start : null,
        // `@name` is people-only sugar (D13): a new name becomes a person.
        tagsFor: (kind) => (kind === 'mention' ? [mergedTarget(this.g, TAG.person)] : []),
      })
      for (const c of creates) {
        if (c.type !== 'node.create') continue
        made.set(titleKey(c.payload.node.title!), c.nodeId)
        if (this.mode.kind === 'edit' && this.mode.id === o.nodeId) {
          if (this.session?.block !== o.nodeId) this.session = { block: o.nodeId, pages: new Set() }
          this.session.pages.add(c.nodeId)
        }
        out.push(c)
      }
    }
    return out
  }

  private mentions(content: string, title: string): boolean {
    const key = titleKey(title)
    return refsOf(content).some((r) => r.title !== null && r.kind !== 'block' && titleKey(r.title) === key)
  }

  /** The caret left `block` (or moved to another): the link session ends, the picker closes. */
  private leaveBlock(next: string | null): void {
    if (this.session && this.session.block !== next) this.session = null
    if (this.picker && this.picker.blockId !== next) this.picker = null
  }

  // ------------------------------------------------------------------ fields and tasks

  /** Set one field of a node (null clears it); one undo unit. */
  setField(id: NodeId, field: string, value: unknown): void {
    this.commitDraft(true)
    const n = this.g.get(id)
    if (!n || n.deletedAt !== null) return
    if (stableJson(n.props[field] ?? null) === stableJson(value ?? null)) return
    this.commit('field', [op.prop(id, field, value ?? null)], {})
  }

  /** Mark a person as "me" (only one at a time); null clears it. */
  setMe(id: NodeId | null): void {
    const ops: OpBody[] = []
    for (const n of this.titles.nodes()) if (n.props[ME_FIELD] === true && n.id !== id) ops.push(op.prop(n.id, ME_FIELD, null))
    if (id && this.g.get(id)?.props[ME_FIELD] !== true) ops.push(op.prop(id, ME_FIELD, true))
    if (ops.length) this.commitOps('me', ops)
    this.uiChanged()
  }

  /** Several ops as one undo unit (e.g. create a person and assign it). */
  commitOps(label: string, ops: readonly OpBody[]): void {
    this.commitDraft(true)
    this.commit(label, ops, {})
  }

  setSchema(tagId: NodeId, schema: Schema): void {
    this.setField(tagId, SCHEMA_FIELD, { fields: schema.fields, extends: schema.extends })
  }

  /** A page's explicit tags (blocks get tags by `#x` in their text). */
  setTags(id: NodeId, tags: readonly NodeId[]): void {
    this.commitDraft(true)
    const n = this.g.get(id)
    if (!n || stableJson(n.tags) === stableJson(tags)) return
    this.commit('tags', [op.tags(id, [...tags])], {})
  }

  /** ⌘⏎ (SPEC §6.3): cycle the task prefix of the block being edited, or of every selected block. */
  cycleTask(): void {
    const m = this.mode
    if (m.kind === 'edit' && !isGhost(m.id)) {
      this.commitDraft(true)
      const before = this.g.must(m.id).content
      const after = cycleStatus(before)
      const caret = this.currentCaret()
      const shift = after.length - before.length
      const pos = (x: number) => Math.max(0, Math.min(after.length, x + shift))
      const caretAfter = caret ? { id: m.id, start: pos(caret.start), end: pos(caret.end) } : null
      this.commit('task', [op.content(m.id, after)], { caretBefore: caret, caretAfter })
      if (caretAfter) this.setCaret(caretAfter, m.root)
    } else if (m.kind === 'select') {
      const ops = this.selectedIds().map((id) => op.content(id, cycleStatus(this.g.must(id).content)))
      this.commit('task', ops, {})
      this.uiChanged()
    }
  }

  /** The checkbox: open → done, done or cancelled → todo. */
  toggleTask(id: NodeId): void {
    this.commitDraft(true)
    const n = this.g.get(id)
    if (!n || n.deletedAt !== null) return
    this.commit('task', [op.content(id, toggleDone(n.content))], {})
  }

  // ------------------------------------------------------------------ links and titles

  // ------------------------------------------------------------------ navigation state

  /** Remember a visit (newest first, each place once, 30 at most). */
  visit(v: Visit): void {
    const same = (x: Visit): boolean => (x.kind === 'node' && v.kind === 'node' ? x.id === v.id : x.kind === 'day' && v.kind === 'day' && x.date === v.date)
    this.recent = [v, ...this.recent.filter((x) => !same(x))].slice(0, 30)
    localStorage.setItem(RECENT_KEY, JSON.stringify(this.recent))
    this.uiChanged()
  }

  setSidebar(patch: Partial<Sidebar>): void {
    this.sidebar = { ...this.sidebar, ...patch }
    localStorage.setItem(SIDEBAR_KEY, JSON.stringify(this.sidebar))
    this.uiChanged()
  }

  /** Open a node (or the calendar) at the top of the right sidebar (⌘-click, ⌘⏎, ⌘⇧K). */
  openInSidebar(p: { kind: 'node'; id: NodeId } | { kind: 'calendar' }): void {
    const key = p.kind === 'node' ? `node:${p.id}` : 'calendar'
    const panes = [{ ...p, key } as Pane, ...this.sidebar.panes.filter((x) => x.key !== key)].slice(0, 8)
    this.setSidebar({ open: true, panes })
  }

  closePane(key: string): void {
    const panes = this.sidebar.panes.filter((x) => x.key !== key)
    this.setSidebar({ panes, open: panes.length > 0 && this.sidebar.open })
  }

  /**
   * Make a block visible by expanding its folded ancestors (landing on a search hit). A view
   * change, not an edit: synced (D14) but not an undo step.
   */
  reveal(id: NodeId): void {
    const ops: OpBody[] = []
    for (let cur = this.g.get(id)?.parentId ?? null; cur !== null; cur = this.g.get(cur)?.parentId ?? null) {
      const n = this.g.get(cur)
      if (n && n.collapsed && n.parentId !== null) ops.push(op.collapse(cur, false))
    }
    if (ops.length) this.sync.local(ops, Date.now())
  }

  /** Build the reference index in idle time after the first paint (it is ready before a page opens). */
  warmInBackground(): void {
    this.search.start()
    const idle = (fn: (budget: number) => void): void => {
      if (typeof requestIdleCallback === 'function') requestIdleCallback((d) => fn(Math.max(2, d.timeRemaining() - 2)), { timeout: 2000 })
      else setTimeout(() => fn(8), 30)
    }
    const step = (budget: number): void => {
      if (!this.refs.warm(budget)) idle(step)
    }
    idle(step)
  }

  /** Rename a page or tag: the title and every ref to it, one undo unit. Returns an error or null. */
  rename(id: NodeId, raw: string): string | null {
    this.commitDraft()
    const r = renameOps(this.g, id, raw)
    if (!r.ok) return r.error
    if (r.ops.length > 0) this.commit('rename', r.ops, {})
    return null
  }

  /**
   * Follow a ref: open its page (in the right sidebar with ⌘/⇧, SPEC §6.2), creating it first if
   * it does not exist (an explicit action).
   */
  openTitle(raw: string, inSidebar = false): void {
    this.commitDraft()
    let id = this.titles.resolve(raw)
    if (!id) {
      const title = normalizeTitle(raw)
      if (titleProblem(title)) return
      const now = Date.now()
      const page = makeNode({ id: uuidv7(), kind: 'page', title, createdAt: now, updatedAt: now })
      this.commit('create page', [op.create(page)], {})
      id = page.id
    }
    if (inSidebar) this.openInSidebar({ kind: 'node', id })
    else this.nav.openNode(id)
  }

  // ------------------------------------------------------------------ picker

  /** Re-detect the ref being typed at the caret; opens, updates or closes the picker. */
  updatePicker(ta: HTMLTextAreaElement): void {
    const id = ta.dataset.id!
    const ctx = ta.selectionStart === ta.selectionEnd ? detect(ta.value, ta.selectionStart) : null
    if (!ctx) {
      if (this.picker) {
        this.picker = null
        this.uiChanged()
      }
      return
    }
    const items = pickerItems(this.g, this.titles, ctx, id)
    this.picker = { ...ctx, blockId: id, items, active: 0 }
    this.uiChanged()
  }

  closePicker(): void {
    if (!this.picker) return
    this.picker = null
    this.uiChanged()
  }

  movePicker(delta: number): void {
    const p = this.picker
    if (!p || p.items.length === 0) return
    this.picker = { ...p, active: (p.active + delta + p.items.length) % p.items.length }
    this.uiChanged()
  }

  setPickerActive(i: number): void {
    if (this.picker && this.picker.active !== i) {
      this.picker = { ...this.picker, active: i }
      this.uiChanged()
    }
  }

  /** Insert the chosen ref, then save at once ("Create" makes the page now: the caret is past it). */
  pickItem(index?: number): void {
    const p = this.picker
    const ta = this.textarea
    this.picker = null
    if (!p || !ta || ta.dataset.id !== p.blockId) return this.uiChanged()
    const item = p.items[index ?? p.active]
    const ctx = detect(ta.value, ta.selectionStart) ?? p
    if (!item) return this.uiChanged()
    let insert: string
    if (item.type === 'block') insert = `((${item.node.id}))`
    else {
      const title = item.type === 'create' ? item.title : item.node.title!
      insert = ctx.kind === 'link' || ctx.kind === 'block' ? `[[${title}]]` : refSyntax(ctx.kind, title, ctx.bracket)
      if ((ctx.kind === 'tag' || ctx.kind === 'mention') && !/^\s/.test(ta.value.slice(ctx.to, ctx.to + 1))) insert += ' '
    }
    ta.setRangeText(insert, ctx.from, ctx.to, 'end')
    this.noteInput(ta)
    this.commitDraft()
    this.uiChanged()
  }

  /**
   * Typing `[` after `[` (or `(` after `(`) closes the pair; typing `]`/`)` in front of the closer
   * steps over it. Returns true if handled (the browser must not insert the character).
   */
  autoPair(ta: HTMLTextAreaElement, data: string | null): boolean {
    if (data === null || ta.selectionStart !== ta.selectionEnd) return false
    const pos = ta.selectionStart
    const v = ta.value
    if ((data === '[' || data === '(') && v[pos - 1] === data) {
      const close = data === '[' ? ']]' : '))'
      if (v.startsWith(close, pos)) return false
      ta.setRangeText(data + close, pos, pos, 'start')
      ta.setSelectionRange(pos + 1, pos + 1)
      this.onInput(ta)
      return true
    }
    if ((data === ']' || data === ')') && v[pos] === data) {
      const open = data === ']' ? '[[' : '(('
      const lineStart = v.lastIndexOf('\n', pos - 1) + 1
      const at = v.lastIndexOf(open, pos)
      if (at >= lineStart && !v.slice(at + 2, pos).includes(data)) {
        ta.setSelectionRange(pos + 1, pos + 1)
        this.updatePicker(ta)
        return true
      }
    }
    return false
  }

  /** Run a structural op in the root of the current mode. */
  run(label: string, fn: (ctx: Ctx) => Result, keepSelection = false): Result | null {
    const root = this.root
    if (root === null) return null
    this.commitDraft(true)
    const caretBefore = this.currentCaret()
    const selBefore = this.mode
    const r = fn(this.ctx(root))
    const caretAfter = r.caret === undefined ? caretBefore : r.caret
    if (r.ops.length > 0) this.commit(label, r.ops, { caretBefore, caretAfter }, this.mode.kind === 'edit' ? (caretAfter ?? null) : null)
    if (r.root !== undefined) {
      this.nav.openNode(r.root)
      return r
    }
    if (r.select && r.select.length > 0) this.setSelection(root, r.select[0]!, r.select[r.select.length - 1]!)
    else if (keepSelection && selBefore.kind === 'select') this.uiChanged()
    else if (r.caret !== undefined) this.setCaret(r.caret, root)
    else if (r.ops.length > 0 && caretBefore) this.setCaret(caretBefore, root)
    else this.uiChanged()
    return r
  }

  private target(): NodeId[] {
    return this.selectedIds()
  }

  split(): void {
    const ta = this.textarea
    if (!ta || this.mode.kind !== 'edit' || isGhost(this.mode.id)) return
    const input = { id: this.mode.id, text: ta.value, start: ta.selectionStart, end: ta.selectionEnd }
    this.run('split', (c) => split(c, input))
  }

  merge(): void {
    const ta = this.textarea
    if (!ta || this.mode.kind !== 'edit' || isGhost(this.mode.id)) return
    const input = { id: this.mode.id, text: ta.value }
    this.run('merge', (c) => merge(c, input))
  }

  indent(): void {
    const ids = this.target()
    this.run('indent', (c) => indent(c, ids), this.mode.kind === 'select')
  }

  outdent(): void {
    const ids = this.target()
    this.run('outdent', (c) => outdent(c, ids), this.mode.kind === 'select')
  }

  move(up: boolean): void {
    const ids = this.target()
    this.run(up ? 'moveUp' : 'moveDown', (c) => (up ? moveUp(c, ids) : moveDown(c, ids)), this.mode.kind === 'select')
  }

  collapse(collapsed: boolean): void {
    const ids = this.target()
    this.run(collapsed ? 'collapse' : 'expand', (c) => setCollapsed(c, ids, collapsed), this.mode.kind === 'select')
  }

  toggle(id: NodeId, root: NodeId): void {
    const n = this.g.must(id)
    this.commitDraft()
    const r = setCollapsed(this.ctx(root), [id], !n.collapsed)
    this.commit(n.collapsed ? 'expand' : 'collapse', r.ops, {})
  }

  remove(): void {
    const ids = this.target()
    this.run('delete', (c) => remove(c, ids))
  }

  duplicate(): void {
    const ids = this.target()
    this.run('duplicate', (c) => duplicate(c, ids))
  }

  zoomIn(id?: string): void {
    const target = id ?? (this.mode.kind === 'edit' ? this.mode.id : this.mode.kind === 'select' ? this.mode.head : null)
    if (target === null || isGhost(target)) return
    this.commitDraft()
    this.nav.openNode(target)
  }

  zoomOut(): void {
    const root = this.root
    if (root === null) return
    this.commitDraft()
    const r = coreZoomOut(this.ctx(root))
    if (r.root === undefined) return
    const parent = this.g.must(r.root)
    if (parent.kind === 'journal' && parent.date) this.nav.openJournal(parent.date)
    else this.nav.openNode(r.root)
  }

  undo(): void {
    this.commitDraft(true)
    try {
      const t = this.history.undo(this.g, Date.now())
      if (t) this.setCaret(t.caretBefore ?? null)
    } catch (e) {
      console.warn('solar: nothing to undo here', e)
    }
  }

  redo(): void {
    this.commitDraft(true)
    try {
      const t = this.history.redo(this.g, Date.now())
      if (t) this.setCaret(t.caretAfter ?? null)
    } catch (e) {
      console.warn('solar: nothing to redo here', e)
    }
  }

  // ------------------------------------------------------------------ navigation

  private editable(): Extract<ViewRow, { type: 'block' | 'ghost' }>[] {
    return this.rows().filter((r): r is Extract<ViewRow, { type: 'block' | 'ghost' }> => r.type === 'block' || r.type === 'ghost')
  }

  /** ↑/↓ at the edge of a block: the previous/next editable row in the view, keeping the column. */
  navigate(up: boolean, column: number): void {
    if (this.mode.kind !== 'edit') return
    const rows = this.editable()
    const i = rows.findIndex((r) => r.id === (this.mode as { id: string }).id)
    const next = rows[i + (up ? -1 : 1)]
    if (i < 0 || !next) return
    this.commitDraft()
    const text = this.textOf(next.id)
    let pos: number
    if (up) {
      const lineStart = text.lastIndexOf('\n') + 1
      pos = Math.min(lineStart + column, text.length)
    } else {
      const firstEnd = text.indexOf('\n')
      pos = Math.min(column, firstEnd < 0 ? text.length : firstEnd)
    }
    this.setCaret({ id: next.id, start: pos, end: pos }, next.root)
  }

  startBlockSelection(up: boolean): void {
    if (this.mode.kind !== 'edit' || isGhost(this.mode.id)) return
    const { id, root } = this.mode
    const rows = this.editable().filter((r) => r.type === 'block' && r.root === root)
    const i = rows.findIndex((r) => r.id === id)
    const next = rows[i + (up ? -1 : 1)]
    this.setSelection(root, id, next ? next.id : id)
  }

  extendSelection(up: boolean): void {
    const m = this.mode
    if (m.kind !== 'select') return
    const rows = this.editable().filter((r) => r.type === 'block' && r.root === m.root)
    const i = rows.findIndex((r) => r.id === m.head)
    const next = rows[i + (up ? -1 : 1)]
    if (next) this.setSelection(m.root, m.anchor, next.id)
  }

  moveSelection(up: boolean): void {
    const m = this.mode
    if (m.kind !== 'select') return
    const ids = this.selectedIds()
    const rows = this.editable().filter((r) => r.type === 'block' && r.root === m.root)
    const edge = rows.findIndex((r) => r.id === (up ? ids[0] : ids[ids.length - 1]))
    const next = rows[edge + (up ? -1 : 1)] ?? rows[edge]
    if (next) this.setSelection(m.root, next.id, next.id)
  }

  shiftClick(id: NodeId, root: NodeId): void {
    const from = this.mode.kind === 'edit' && !isGhost(this.mode.id) ? this.mode.id : this.mode.kind === 'select' ? this.mode.anchor : id
    const sameRoot = this.mode.kind !== 'idle' && this.mode.root === root
    this.setSelection(root, sameRoot ? from : id, id)
  }

  selectAll(): void {
    const root = this.root
    if (root === null) return
    const rows = this.editable().filter((r) => r.type === 'block' && r.root === root)
    if (rows.length) this.setSelection(root, rows[0]!.id, rows[rows.length - 1]!.id)
  }

  /** Esc: leave block (select it) → clear selection → zoom out (SPEC §6.3). */
  escape(): void {
    const m = this.mode
    if (this.picker) return this.closePicker()
    if (m.kind === 'edit') {
      this.commitDraft()
      if (isGhost(m.id) || m.id === m.root) this.idle()
      else this.setSelection(m.root, m.id, m.id)
    } else if (m.kind === 'select') {
      this.idle()
    }
  }
}
